import React from "react";
import { useQuery } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { money, shortDate } from "@/lib/format";
import { toast } from "sonner";
import { Receipt, TrendUp, ArrowsClockwise, FileArrowDown } from "@phosphor-icons/react";

export default function Billing() {
  const { data: txns } = useQuery({ queryKey: ["billing-txns"], queryFn: async () => (await api.get("/billing/transactions")).data });
  const { data: summary } = useQuery({ queryKey: ["billing-summary"], queryFn: async () => (await api.get("/billing/summary")).data });
  const rows = txns?.transactions || [];

  const download = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href; a.download = filename; a.click();
      URL.revokeObjectURL(href);
    } catch (e) { toast.error(apiError(e)); }
  };
  const thisMonth = new Date().toISOString().slice(0, 7);

  const cards = [
    { label: "Total Spent", value: money(summary?.total_spent_cents || 0), icon: Receipt },
    { label: "Total Added", value: money(summary?.total_added_cents || 0), icon: TrendUp },
    { label: "Transactions", value: summary?.transaction_count || 0, icon: ArrowsClockwise },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Billing & Usage</h1>
          <p className="mt-2 text-muted-foreground">Transactions, invoices and statements.</p>
        </div>
        <button data-testid="download-statement" onClick={() => download(`/billing/statement?month=${thisMonth}`, `statement-${thisMonth}.pdf`)}
          className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2.5 text-sm font-medium transition-colors hover:border-primary">
          <FileArrowDown size={16} className="text-primary" weight="duotone" /> Download statement ({thisMonth})
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">{c.label}</p>
              <c.icon size={18} className="text-primary" weight="duotone" />
            </div>
            <p className="mt-2 font-mono-metric text-2xl font-semibold">{c.value}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-2xl border border-border bg-card">
        <div className="grid grid-cols-12 gap-2 border-b border-border px-4 py-3 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          <div className="col-span-4">Description</div>
          <div className="col-span-2">Product</div>
          <div className="col-span-2">Provider</div>
          <div className="col-span-2 text-right">Amount</div>
          <div className="col-span-2 text-right">Date</div>
        </div>
        {rows.length === 0 && <p className="px-4 py-10 text-center text-sm text-muted-foreground">No transactions yet</p>}
        {rows.map((t, i) => (
          <div key={i} data-testid={`txn-row-${i}`} className="grid grid-cols-12 items-center gap-2 border-b border-border px-4 py-3 text-sm last:border-0">
            <div className="col-span-4 truncate">{t.description || t.product}</div>
            <div className="col-span-2 truncate capitalize text-muted-foreground">{t.product?.replace(/_/g, " ")}</div>
            <div className="col-span-2 truncate text-muted-foreground">{t.provider}</div>
            <div className={`col-span-2 text-right font-mono-metric font-semibold ${t.direction === "credit" ? "text-emerald-600" : ""}`}>
              {t.direction === "credit" ? "+" : "−"}{money(t.amount_cents, t.currency)}
            </div>
            <div className="col-span-2 flex items-center justify-end gap-2 text-right text-xs text-muted-foreground">
              {shortDate(t.created_at)}
              <button data-testid={`invoice-dl-${i}`} title="Download invoice PDF"
                onClick={() => download(`/billing/invoice/${t.txn_id}`, `invoice-${t.txn_id}.pdf`)}
                className="text-muted-foreground transition-colors hover:text-primary">
                <FileArrowDown size={16} weight="duotone" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
